/**
 * 요금 정산 차트 — 색상·축·강조 SSOT
 */

/** 저채도 팔레트 (UI $acc/$ok/$warn의 muted 버전) */
export const CHART_PALETTE = {
    primary: "#6B8DB8",
    secondary: "#94A3B8",
    neutral: "#B8C4D4",
    series: ["#7A9BC4", "#6FA88A", "#C4A574"],
    energy: {
        Elec: "#7A9BC4",
        Gas: "#6FA88A",
        Water: "#6A9EAE",
    },
    spark: {
        default: "#7A9BC4",
        Elec: "#7A9BC4",
        Gas: "#6FA88A",
        Water: "#6A9EAE",
    },
    dailyBar: "rgba(107, 141, 184, 0.52)",
    dailyBarHover: "rgba(107, 141, 184, 0.82)",
};

export const CHART_AXIS = {
    grid: { left: 48, right: 16, top: 32, bottom: 32 },
    splitLine: "#EEF1F5",
    axisLine: "#E6E9EF",
    label: "#64748B",
    name: "#94A3B8",
};

export const CHART_HIGHLIGHT = {
    markLineColor: "#CBD5E1",
    axisFontWeight: 600,
};

/** 에너지원별 색 (스파크라인·일별 차트) */
export function energyChartColor(energy) {
    return CHART_PALETTE.energy[energy] || CHART_PALETTE.spark.default;
}

export function energySparkColor(energy) {
    return CHART_PALETTE.spark[energy] || CHART_PALETTE.spark.default;
}

/** 시리즈 N개용 순환 팔레트 */
export function seriesColors(count) {
    const base = CHART_PALETTE.series;
    return Array.from({ length: count }, (_, i) => base[i % base.length]);
}

/** role별 라인 스타일 */
export function lineSeriesStyle(role, color) {
    if (role === "secondary") {
        return {
            lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.75 },
            itemStyle: { color, opacity: 0.75 },
            symbol: "circle",
            symbolSize: 4,
        };
    }
    return {
        lineStyle: { color, width: 2.2 },
        itemStyle: { color },
        symbol: "circle",
        symbolSize: 5,
    };
}

/** 선택 월 세로 기준선 (첫 시리즈에만) */
export function buildHighlightMarkLine(highlightIndex) {
    if (highlightIndex < 0) return undefined;
    return {
        silent: true,
        symbol: "none",
        label: { show: false },
        lineStyle: {
            color: CHART_HIGHLIGHT.markLineColor,
            type: "dashed",
            width: 1,
        },
        data: [{ xAxis: highlightIndex }],
    };
}

/** X축 라벨 — 선택 월만 약한 강조 */
export function buildXAxisLabelFormatter(highlightIndex, textStyleFn) {
    return {
        ...textStyleFn(),
        fontSize: 10,
        color: CHART_AXIS.label,
        formatter: (value, index) => {
            if (index === highlightIndex) return `{hl|${value}}`;
            return value;
        },
        rich: {
            hl: {
                color: CHART_AXIS.label,
                fontWeight: CHART_HIGHLIGHT.axisFontWeight,
                fontSize: 10,
            },
        },
    };
}

export function baseChartOption(textStyleFn) {
    const ts = (extra) => textStyleFn(extra);
    return {
        textStyle: ts(),
        legend: {
            top: 0,
            itemWidth: 12,
            itemHeight: 8,
            textStyle: ts({ fontSize: 11, color: CHART_AXIS.label }),
        },
        xAxis: {
            axisLine: { lineStyle: { color: CHART_AXIS.axisLine } },
            axisTick: { show: false },
        },
        yAxis: {
            splitLine: { lineStyle: { color: CHART_AXIS.splitLine } },
            axisLine: { show: false },
            axisTick: { show: false },
            nameTextStyle: ts({ fontSize: 10, color: CHART_AXIS.name }),
            axisLabel: ts({ fontSize: 10, color: CHART_AXIS.label }),
        },
    };
}

/** 일별 바 itemStyle */
export function dailyBarItemStyle(color) {
    if (!color) {
        return { color: CHART_PALETTE.dailyBar, borderRadius: [3, 3, 0, 0] };
    }
    const fill = color.startsWith("rgba") ? color : `${color}85`;
    return { color: fill, borderRadius: [3, 3, 0, 0] };
}
